import React, { useState } from 'react';
import { useWallet } from '@/contexts/WalletContext';
import styles from '@/styles/components/wallet/WalletHeader.module.css';


const WalletHeader: React.FC = () => {
  const { state, disconnect } = useWallet();
  const [showDisconnectMenu, setShowDisconnectMenu] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);



  /**
   * Truncates wallet address to show first 6 and last 4 characters
   * Requirement 8.1: Display wallet address with truncation
   */
  const truncateAddress = (address: string): string => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  /**
   * Handles copying wallet address to clipboard with user feedback
   * Requirements 8.2: Copy-to-clipboard functionality with user feedback
   */
  const handleCopyAddress = async () => {
    if (state.address) {
      try {
        await navigator.clipboard.writeText(state.address);
        setCopyFeedback('Copied!');
        setTimeout(() => setCopyFeedback(null), 2000);
      } catch (error) {
        console.error('Failed to copy address:', error);
        setCopyFeedback('Failed to copy');
        setTimeout(() => setCopyFeedback(null), 2000);
      }
    }
  };

  /**
   * Handles wallet disconnection with confirmation
   * Requirements 8.4, 8.5, 8.6, 8.7: Disconnect confirmation and cleanup
   */
  const handleDisconnectConfirm = async () => {
    setIsDisconnecting(true);
    try {
      await disconnect();
      setShowDisconnectConfirm(false);
      setShowDisconnectMenu(false);
    } catch (error) {
      console.error('Failed to disconnect:', error);
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (!state.isConnected || !state.address) {
    return null;
  }

  return (
    <>
      {/* Profile Section */}
      <div className={styles.profile} data-testid="wallet-header">
        <div className={styles.pic}>
          <div className={styles.network}>
            <div className={styles.wallet}>
              <img
                className={styles.metamaskIcon}
                src="/icons/profile_page/wallet/metamask.svg"
                alt="MetaMask"
              />
            </div>
          </div>
        </div>
        
        <div className={styles.details}>
          <div className={styles.desc}>
            <div className={styles.addressText} data-testid="wallet-address">
              {truncateAddress(state.address)}
            </div>
            <button
              className={styles.copyButton}
              onClick={handleCopyAddress}
              title="Copy address"
              data-testid="copy-address-button"
            >
              <svg className={styles.icon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
              {copyFeedback && (
                <div className={styles.feedback} data-testid="copy-feedback">
                  {copyFeedback}
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <button
          className={styles.menuButton}
          onClick={() => setShowDisconnectMenu(!showDisconnectMenu)}
          title="Wallet options"
          data-testid="wallet-menu-button"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="5" cy="12" r="1"></circle>
            <circle cx="12" cy="12" r="1"></circle>
            <circle cx="19" cy="12" r="1"></circle>
          </svg>
        </button>

        {/* Three dots menu - Requirement 8.3 */}
        {showDisconnectMenu && (
          <div className={styles.dropdown} data-testid="disconnect-menu">
            <button
              className={styles.dropdownItem}
              onClick={() => {
                setShowDisconnectConfirm(true);
                setShowDisconnectMenu(false);
              }}
              data-testid="disconnect-menu-item"
            >
              Disconnect
            </button>
          </div>
        )}
      </div>

      {/* Disconnect Confirmation Dialog - Requirements 8.4, 8.5 */}
      {showDisconnectConfirm && (
        <div className={styles.confirmDialog} data-testid="disconnect-confirmation">
          <div className={styles.confirmContent}>
            <h3 className={styles.confirmTitle}>Disconnect Wallet</h3>
            <p className={styles.confirmDesc}>
              Are you sure you want to disconnect your wallet? You'll need to reconnect to view your balance and make transactions.
            </p>
            <div className={styles.confirmButtons}>
              <button
                className={`${styles.buttonBase} ${styles.secondaryButton}`}
                onClick={() => setShowDisconnectConfirm(false)}
                disabled={isDisconnecting}
                data-testid="cancel-disconnect-button"
              >
                Cancel
              </button>
              <button
                className={`${styles.buttonBase} ${styles.dangerButton}`}
                style={{
                  opacity: isDisconnecting ? 0.6 : 1,
                  cursor: isDisconnecting ? 'not-allowed' : 'pointer',
                }}
                onClick={handleDisconnectConfirm}
                disabled={isDisconnecting}
                data-testid="confirm-disconnect-button"
              >
                {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDisconnectMenu && (
        <div
          className={styles.dropdownOverlay}
          onClick={() => setShowDisconnectMenu(false)}
          data-testid="dropdown-overlay"
        />
      )}
    </>
  );
};

export default WalletHeader;